import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import Button from '@/components/common/Button'
import { 
  Plus, 
  FileText, 
  Clock, 
  CheckCircle, 
  BarChart3,
  TrendingUp,
  Users,
  Globe,
  Calendar,
  ArrowRight,
  Target,
  Zap,
  Award,
  Database
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import GettingStartedCard, { ResumeSetupPill } from '@/components/GettingStartedCard'

interface ProjectStats {
  totalProjects: number
  inProgress: number
  completed: number
  totalWords: number
  totalSegments: number
  completedSegments: number
  languagePairs: number
  recentActivity: number
}

interface RecentProject {
  id: string
  name: string
  source_language: string
  target_language: string
  total_segments: number
  completed_segments: number
  word_count: number
  created_at: string
  updated_at: string
}

const Dashboard: React.FC = () => {
  const { user } = useAuth()
  const [stats, setStats] = useState<ProjectStats>({
    totalProjects: 0,
    inProgress: 0,
    completed: 0,
    totalWords: 0,
    totalSegments: 0,
    completedSegments: 0,
    languagePairs: 0,
    recentActivity: 0
  })
  const [recentProjects, setRecentProjects] = useState<RecentProject[]>([])
  const [loading, setLoading] = useState(true)

  // Load dashboard statistics
  const loadDashboardStats = async () => {
    if (!user) return

    try {
      console.log('� Loading dashboard statistics...')

      // Get all projects for user
      const { data: projects, error: projectsError } = await supabase
        .from('cat_projects')
        .select('*')
        .eq('owner_id', user.id)
        .order('updated_at', { ascending: false })

      if (projectsError) {
        console.error('❌ Failed to load projects:', projectsError)
        return
      }

      console.log('✅ Projects loaded:', projects?.length || 0)

      // Calculate statistics
      const totalProjects = projects?.length || 0
      const totalWords = projects?.reduce((sum, p) => sum + (p.word_count || 0), 0) || 0
      const totalSegments = projects?.reduce((sum, p) => sum + (p.total_segments || 0), 0) || 0
      const completedSegments = projects?.reduce((sum, p) => sum + (p.completed_segments || 0), 0) || 0
      
      // Count projects by completion status
      const completed = projects?.filter(p => p.completed_segments >= p.total_segments).length || 0
      const inProgress = projects?.filter(p => p.completed_segments > 0 && p.completed_segments < p.total_segments).length || 0
      
      // Count unique language pairs
      const languagePairSet = new Set(projects?.map(p => `${p.source_language}-${p.target_language}`) || [])
      const languagePairs = languagePairSet.size

      // Count recent activity (projects updated in last 7 days)
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
      const recentActivity = projects?.filter(p => new Date(p.updated_at) > sevenDaysAgo).length || 0

      setStats({
        totalProjects,
        inProgress,
        completed,
        totalWords,
        totalSegments,
        completedSegments,
        languagePairs,
        recentActivity
      })

      // Set recent projects (last 5)
      setRecentProjects(projects?.slice(0, 5) || [])

      console.log('� Dashboard stats calculated:', {
        totalProjects,
        inProgress,
        completed,
        totalWords,
        languagePairs
      })

    } catch (error) {
      console.error('❌ Failed to load dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      loadDashboardStats()
    }
  }, [user])

  // First name for the greeting: the profile name, else the start of the email address.
  const fullName = (user?.name || '').trim()
  const emailName = user?.email?.split('@')[0] || ''
  const firstName = fullName
    ? fullName.split(/\s+/)[0]
    : emailName
      ? emailName.charAt(0).toUpperCase() + emailName.slice(1)
      : 'there'

  // Calculate overall progress
  const overallProgress = stats.totalSegments > 0 
    ? Math.round((stats.completedSegments / stats.totalSegments) * 100)
    : 0

  // Format language names
  const formatLanguage = (code: string) => {
    const langNames: Record<string, string> = {
      'en': 'English', 'es': 'Spanish', 'fr': 'French', 'de': 'German',
      'it': 'Italian', 'pt': 'Portuguese', 'pt-br': 'Portuguese (BR)', 'ru': 'Russian',
      'zh': 'Chinese', 'ja': 'Japanese', 'ko': 'Korean', 'ar': 'Arabic'
    }
    return langNames[code] || code.toUpperCase()
  }

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`
    return `${Math.floor(diffInHours / 168)}w ago`
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">
              {stats.totalProjects === 0 ? 'Welcome' : 'Welcome back'}, {firstName}!
            </h1>
            <ResumeSetupPill />
          </div>
          <p className="text-gray-800 mt-1">
            Here's what's happening with your translation projects.
          </p>
        </div>
        <Link to="/projects/new">
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
            Create Project
          </Button>
        </Link>
      </div>

      {/* Main Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Projects */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">Total Projects</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalProjects}</p>
            </div>
            <div className="h-12 w-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Calendar className="h-4 w-4 text-gray-800 mr-1" />
            <span className="text-gray-800">
              {stats.recentActivity} active this week
            </span>
          </div>
        </div>

        {/* In Progress */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">In Progress</p>
              <p className="text-3xl font-bold text-gold-600">{stats.inProgress}</p>
            </div>
            <div className="h-12 w-12 bg-gold-50 rounded-lg flex items-center justify-center">
              <Clock className="h-6 w-6 text-gold-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-800 mb-1">
              <span>Overall Progress</span>
              <span>{overallProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gold-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Completed */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">Completed</p>
              <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <div className="h-12 w-12 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Award className="h-4 w-4 text-gray-800 mr-1" />
            <span className="text-gray-800">
              {stats.completedSegments.toLocaleString()} segments done
            </span>
          </div>
        </div>

        {/* Total Words */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">Total Words</p>
              <p className="text-3xl font-bold text-purple-600">
                {stats.totalWords.toLocaleString()}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-50 rounded-lg flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Globe className="h-4 w-4 text-gray-800 mr-1" />
            <span className="text-gray-800">
              {stats.languagePairs} language pairs
            </span>
          </div>
        </div>
      </div>

      {user && (
        <GettingStartedCard
          userId={user.id}
          firstName={firstName}
          hasProjects={stats.totalProjects > 0}
          hasConfirmedSegment={stats.completedSegments > 0}
        />
      )}

      {/* Recent Projects and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Projects */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Projects</h2>
              <Link to="/projects">
                <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="h-4 w-4" />}>
                  View All
                </Button>
              </Link>
            </div>
          </div>
          
          <div className="p-6">
            {recentProjects.length > 0 ? (
              <div className="space-y-6">
                {/* Group projects by language pair */}
                {(() => {
                  const groups = recentProjects.reduce((acc, project) => {
                    const key = `${project.source_language}→${project.target_language}`
                    if (!acc[key]) acc[key] = []
                    acc[key].push(project)
                    return acc
                  }, {} as Record<string, typeof recentProjects>)

                  return Object.entries(groups).map(([pair, projects]) => {
                    const [src, tgt] = pair.split('→')
                    return (
                      <div key={pair}>
                        {/* Language pair header */}
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex items-center gap-1.5 px-3 py-1 bg-slate-800 rounded-full">
                            <Globe className="h-3 w-3 text-gold-400" />
                            <span className="text-xs font-semibold text-white">
                              {formatLanguage(src)} → {formatLanguage(tgt)}
                            </span>
                            <span className="text-xs text-slate-400 ml-1">
                              {projects.length} project{projects.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex-1 h-px bg-gray-200" />
                        </div>

                        {/* Projects in this language pair */}
                        <div className="space-y-2 pl-2">
                          {projects.map((project) => (
                            <div
                              key={project.id}
                              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-100"
                            >
                              <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-gray-900 truncate">{project.name}</h3>
                                <div className="flex items-center space-x-3 mt-1 text-sm text-gray-600">
                                  <span>{project.total_segments} segments</span>
                                  <span>·</span>
                                  <span>{project.word_count.toLocaleString()} words</span>
                                  <span>·</span>
                                  <span>{formatTimeAgo(project.updated_at)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 ml-4 shrink-0">
                                <div className="text-right">
                                  <div className="text-sm font-bold text-gray-900">
                                    {project.total_segments > 0
                                      ? Math.round((project.completed_segments / project.total_segments) * 100)
                                      : 0}%
                                  </div>
                                  <div className="w-16 h-1.5 bg-gray-200 rounded-full mt-1">
                                    <div
                                      className="h-1.5 bg-gold-500 rounded-full"
                                      style={{ width: `${project.total_segments > 0 ? Math.round((project.completed_segments / project.total_segments) * 100) : 0}%` }}
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Link to={`/projects/${project.id}/new-version`}>
                                    <button className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gold-400 text-gold-700 bg-gold-50 hover:bg-gold-100 transition-colors whitespace-nowrap">
                                      New Version
                                    </button>
                                  </Link>
                                  <Link to={`/projects/${project.id}/editor`}>
                                    <Button size="sm" variant="secondary">
                                      Continue
                                    </Button>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })
                })()}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-800 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No projects yet</h3>
                <p className="text-gray-700 mb-4">
                  Get started by creating your first translation project.
                </p>
                <Link to="/projects/new">
                  <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
                    Create Project
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
          </div>
          
          <div className="p-6 space-y-4">
            <Link to="/projects/new">
              <div className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group cursor-pointer">
                <div className="h-10 w-10 bg-blue-500 rounded-lg flex items-center justify-center mr-4">
                  <Plus className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 group-hover:text-blue-600">
                    Create Project
                  </h3>
                  <p className="text-sm text-gray-800">
                    Create a new translation project
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-800 group-hover:text-blue-600" />
              </div>
            </Link>

            <Link to="/projects">
              <div className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors group cursor-pointer">
                <div className="h-10 w-10 bg-purple-500 rounded-lg flex items-center justify-center mr-4">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 group-hover:text-purple-600">
                    Browse Projects
                  </h3>
                  <p className="text-sm text-gray-800">
                    View and manage all projects
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-800 group-hover:text-purple-600" />
              </div>
            </Link>

            {/* Translation Memory - WORKING (Phase 3 installed) */}
            <Link to="/translation-memory">
              <div className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors group cursor-pointer">
                <div className="h-10 w-10 bg-green-500 rounded-lg flex items-center justify-center mr-4">
                  <Database className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 group-hover:text-green-600">
                    Translation Memory
                  </h3>
                  <p className="text-sm text-gray-800">
                    Browse and manage TM entries
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-800 group-hover:text-green-600" />
              </div>
            </Link>

            {/* Coming Soon - Coming Soon (Phase 4 not installed) */}
            <div className="flex items-center p-4 bg-gray-50 rounded-lg">
              <div className="h-10 w-10 bg-gray-400 rounded-lg flex items-center justify-center mr-4">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-gray-700">Coming Soon</h3>
                <p className="text-sm text-gray-800">
                  Coming soon
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  )
}

export default Dashboard